import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { InlineAIPanel } from './InlineAIPanel';

interface InlineAIPanelPortalProps {
  cardRef: React.RefObject<HTMLDivElement>;
  selectedText: string;
  fullContent: string;
  onReplace: (newText: string) => void;
  onInsert: (newText: string) => void;
  onClose: () => void;
}

const PANEL_WIDTH = 320;
const GAP = 12;
const VIEWPORT_PADDING = 16;

export function InlineAIPanelPortal({
  cardRef,
  selectedText,
  fullContent,
  onReplace,
  onInsert,
  onClose,
}: InlineAIPanelPortalProps) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const calculatePosition = useCallback(() => {
    if (!cardRef.current) return null;

    const cardRect = cardRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate available space on right and left
    const spaceOnRight = viewportWidth - cardRect.right - VIEWPORT_PADDING;
    const spaceOnLeft = cardRect.left - VIEWPORT_PADDING;

    let left: number;
    
    // Prefer right side, but flip to left if not enough space
    if (spaceOnRight >= PANEL_WIDTH + GAP) {
      // Position to the right of the card
      left = cardRect.right + GAP;
    } else if (spaceOnLeft >= PANEL_WIDTH + GAP) {
      // Position to the left of the card
      left = cardRect.left - PANEL_WIDTH - GAP;
    } else {
      // Not enough space on either side, position on the side with more space
      if (spaceOnRight >= spaceOnLeft) {
        left = Math.max(VIEWPORT_PADDING, viewportWidth - PANEL_WIDTH - VIEWPORT_PADDING);
      } else {
        left = VIEWPORT_PADDING;
      }
    }

    // Vertical positioning: align with top of card, but ensure it stays in viewport
    let top = cardRect.top;
    
    // Estimate panel height (will be constrained by CSS max-height)
    const estimatedPanelHeight = 400;
    
    // Ensure panel doesn't go below viewport
    if (top + estimatedPanelHeight > viewportHeight - VIEWPORT_PADDING) {
      top = Math.max(VIEWPORT_PADDING, viewportHeight - estimatedPanelHeight - VIEWPORT_PADDING);
    }
    
    // Ensure panel doesn't go above viewport
    if (top < VIEWPORT_PADDING) {
      top = VIEWPORT_PADDING;
    }

    return { top, left };
  }, [cardRef]);

  // Calculate initial position and update on resize/scroll
  useEffect(() => {
    const updatePosition = () => {
      const newPos = calculatePosition();
      if (newPos) {
        setPosition(newPos);
      }
    };

    updatePosition();

    // Update position on window resize
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [calculatePosition]);

  if (!position) return null;

  return createPortal(
    <InlineAIPanel
      selectedText={selectedText}
      fullContent={fullContent}
      onReplace={onReplace}
      onInsert={onInsert}
      onClose={onClose}
      position={position}
      isPortal={true}
    />,
    document.body
  );
}
